// =======================
// CORE DEPENDENCIES
// =======================
import React, { useState, useEffect } from "react";
import jsPDF from "jspdf";
import { toast } from "react-toastify";
import { saveAs } from "file-saver";


// =======================
// MODAL
// =======================
import Modal from "react-modal";


// =======================
// ICONS
// =======================
import { FaSolarPanel, FaBatteryFull } from "react-icons/fa";

// =======================
// SOCIAL SHARING LIBRARY
// =======================
import {
  FacebookShareButton,
  TwitterShareButton,
  LinkedinShareButton,
  WhatsappShareButton,
  FacebookIcon,
  TwitterIcon,
  LinkedinIcon,
  WhatsappIcon,
} from "react-share";

// =======================
// STYLES
// =======================
import "../styles/SolarSizingCalculator.css";

Modal.setAppElement("#root"); // needed for accessibility


// =======================
// PAYSTACK SCRIPT LOADER
// =======================
const loadPaystackScript = () => {
  return new Promise((resolve, reject) => {
    if (document.getElementById("paystack-script")) return resolve();
    const script = document.createElement("script");
    script.id = "paystack-script";
    script.src = "https://js.paystack.co/v1/inline.js";
    script.onload = resolve;
    script.onerror = reject;
    document.body.appendChild(script);
  });
};

// =======================
// HELPERS (dates / storage)
// =======================
  const daysBetween = (a, b) => {
  const ms = b.getTime() - a.getTime();
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
  };

  const addDays = (date, numDays) => {
  const d = new Date(date);
  d.setDate(d.getDate() + numDays);
  return d;
  };

// =======================
// MAIN COMPONENT
// =======================
  export default function SolarSizingCalculator() {
  const [items, setItems] = useState([{ name: "", watts: "", hours: "", quantity: "" }]);
  const [psh, setPsh] = useState("");
  const [panelWattage, setPanelWattage] = useState("");
  
  const [results, setResults] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [gettingPSH, setGettingPSH] = useState(false);

  // Free trial & subscription tracking
  const [calcCount, setCalcCount] = useState(() => parseInt(localStorage.getItem("calcCount") || "0", 10));
  const trialLimit = 3;
  const [isSubscribed, setIsSubscribed] = useState(localStorage.getItem("isSubscribed") === "true");
  const [subscriptionExpiry, setSubscriptionExpiry] = useState(localStorage.getItem("subscriptionExpiry") || "");
  const [email, setEmail] = useState(localStorage.getItem("userEmail") || "");
  const [lastReminderKey, setLastReminderKey] = useState(localStorage.getItem("lastReminderKey") || "");

  // UI aux
  const [subLoading, setSubLoading] = useState(false);

  const shareUrl = "https://globisun-app-1.onrender.com";
  const shareTitle = "Check out this Solar Sizing Calculation Result!";

  const panelOptions = [100, 150, 200, 250, 300, 350, 400, 450, 500, 550, 600, 650, 670,700, 730, 750, 790 ];
  

  const [batteryType, setBatteryType] = useState(""); // "tubular" | "lithium"



  // Load Paystack
  useEffect(() => {
    loadPaystackScript().catch(() => {});
  }, []);

  // On mount, validate subscription by expiry
      useEffect(() => {
      if (subscriptionExpiry) {
      const now = new Date();
      const exp = new Date(subscriptionExpiry);
      if (now > exp) {
        // expired
        setIsSubscribed(false);
        localStorage.setItem("isSubscribed", "false");
      } else {
        setIsSubscribed(true);
        localStorage.setItem("isSubscribed", "true");
      }
    }
  }, []); // run once

  // Persist email to localStorage
  useEffect(() => {
    if (email) localStorage.setItem("userEmail", email);
  }, [email]);

  // Check reminder (runs once on mount, and again if expiry changes)
  useEffect(() => {
    if (!subscriptionExpiry) return;
    const now = new Date();
    const exp = new Date(subscriptionExpiry);
    const daysLeft = daysBetween(now, exp);

    // Fire a reminder exactly at 5 days left, only once per expiry value
    const reminderKey = `reminder-${exp.toISOString()}`;
    if (daysLeft === 5 && lastReminderKey !== reminderKey) {
      toast.warn("⚠️ Heads up! Your subscription will expire in 5 days. Consider renewing to keep using the calculator without limits.");
      localStorage.setItem("lastReminderKey", reminderKey);
      setLastReminderKey(reminderKey);
    }

    // Optional: periodic re-check (e.g., every 6 hours while app is open)
      const interval = setInterval(() => {
      const now2 = new Date();
      const dLeft = daysBetween(now2, new Date(subscriptionExpiry));
      const reminderKey2 = `reminder-${new Date(subscriptionExpiry).toISOString()}`;
      if (dLeft === 5 && localStorage.getItem("lastReminderKey") !== reminderKey2) {
       toast.warn("⚠️ Heads up! Your subscription will expire in 5 days. Consider renewing to keep using the calculator without limits.");
        localStorage.setItem("lastReminderKey", reminderKey2);
        setLastReminderKey(reminderKey2);
      }

      // Auto flip to not subscribed when expired
      if (dLeft <= 0) {
        setIsSubscribed(false);
        localStorage.setItem("isSubscribed", "false");
      }
    }, 6 * 60 * 60 * 1000);

    return () => clearInterval(interval);
  }, [subscriptionExpiry, lastReminderKey]);

  
  
  // -------------- Existing handlers --------------
  const handleChange = (index, field, value) => {
    const updated = [...items];
    updated[index][field] = value;
    setItems(updated);
  };

  const addItem = () => {
    setItems([...items, { name: "", watts: "", hours: "", quantity: "" }]);
  };

  const removeItem = (index) => {
    const updated = items.filter((_, i) => i !== index);
    setItems(updated);
  };

  const allInputsFilled = () => {
  if (!psh || !panelWattage || !batteryType) return false;
  for (let item of items) {
    if (!item.name || !item.watts || !item.hours || !item.quantity) return false;
  }
  return true;
};

  
  // -------------- Subscription helpers --------------
  const activateSubscription = (daysToAdd) => {
    const now = new Date();
    // If already subscribed, extend from current expiry if it's in the future
    let base = now;
    if (subscriptionExpiry) {
      const currentExp = new Date(subscriptionExpiry);
      if (currentExp > now) base = currentExp;
    }
    const newExpiry = addDays(base, daysToAdd);
    localStorage.setItem("isSubscribed", "true");
    localStorage.setItem("subscriptionExpiry", newExpiry.toISOString());
    setIsSubscribed(true);
    setSubscriptionExpiry(newExpiry.toISOString());

    // Reset calcCount on start (optional; comment out if you prefer to keep)
    localStorage.setItem("calcCount", "0");
    setCalcCount(0);

    // Reset reminder marker for this new expiry
    const reminderKey = `reminder-${newExpiry.toISOString()}`;
    localStorage.setItem("lastReminderKey", ""); // allow firing at 5 days again
    setLastReminderKey("");

    toast.success(`✅ Subscription activated! Expires on ${newExpiry.toDateString()}.`);
  };

  const [showModal, setShowModal] = useState(false);
  const [plans] = useState([
  { label: "1 Month", days: 30, amountNGN: 1000 },
  { label: "3 Months", days: 90, amountNGN: 2500 },
  { label: "6 Months", days: 180, amountNGN: 5500 },
  { label: "1 Year", days: 365, amountNGN: 10000 },
]);

// Email modal & plan tracking
const [showEmailModal, setShowEmailModal] = useState(false); // controls email modal visibility
const [tempEmail, setTempEmail] = useState(""); // holds the typed email temporarily
const [pendingPlan, setPendingPlan] = useState(null); // holds the plan user selected while entering email


const openSubscriptionModal = () => {
  setShowModal(true);
};

const handlePlanSelect = (plan) => {
  setShowModal(false); // close the plan modal
  if (!email) {
    // If email is missing, open email modal
    setTempEmail("");       // clear previous input
    setPendingPlan(plan);   // remember the plan user clicked
    setShowEmailModal(true); 
  } else {
    initiatePayment(plan); // email exists, go straight to payment
  }
};



  const initiatePayment = (plan) => {
    if (!window.PaystackPop) {
      toast.warn("⚠️ Payment system not ready yet. Please try again in a moment.");
      return;
    }
    if (!email) {
      const typed = window.prompt("Enter your email for the receipt & reminders:");
      if (!typed) return;
      setEmail(typed);
      localStorage.setItem("userEmail", typed);
    }

    setSubLoading(true);
    try {
      const handler = window.PaystackPop.setup({
        key: "pk_live_7264851bb6628e9752b2aafb66c35e9064d1efe7", // <-- replace
        email: (email || "customer@example.com").trim(),
        amount: plan.amountNGN * 100, // Paystack expects lowest currency unit (kobo)
        currency: "NGN",
        ref: `SOLAR-${Date.now()}`,
        callback: (response) => {
          // Ideally verify on your backend using response.reference
          activateSubscription(plan.days);
          setSubLoading(false);
        },
        onClose: () => {
          setSubLoading(false);
          // User closed the payment modal
        },
      });
      handler.openIframe();
    } catch (e) {
      setSubLoading(false);
      toast.error("❌ Failed to open Paystack. Please try again.");
    }
  };



  const getMarketInverter = (kva) => {
  if (kva <= 1) return 1; // anything below 1 kVA rounds up to 1

  let current = 1;
  while (true) {
    if (kva <= current) return current;
    if (current % 1 === 0) current += 0.5; // whole number → add 0.5
    else current = Math.ceil(current);     // .5 → next whole number
  }
};


  
  // -------------- Calculation --------------
  const calculateSizing = () => {
    // Enforce free trial / subscription
    const now = new Date();
    if (subscriptionExpiry) {
      const exp = new Date(subscriptionExpiry);
      if (now > exp) {
        setIsSubscribed(false);
        localStorage.setItem("isSubscribed", "false");
      }
    }

    if (!isSubscribed && calcCount >= trialLimit) {
      toast.info("🔒 You have reached your free limit (3 calculations). Please subscribe to continue.");
      openSubscriptionModal();
      return;
    }

    if (!allInputsFilled()) {
      toast.error("⚠️ Please fill all required fields including all appliance details, PSH, panel and battery selections.");
      return;
    }

    if (!isSubscribed) {
      const newCount = calcCount + 1;
      setCalcCount(newCount);
      localStorage.setItem("calcCount", String(newCount));
    }

    

  setCalculating(true);

setTimeout(() => {
  // ---------------- Load Calculation ----------------
  const totalLoadW = items.reduce((sum, it) => {
    const w = parseFloat(it.watts) || 0;
    const q = parseInt(it.quantity) || 1;
    return sum + w * q;
  }, 0);

  const totalLoadWh = items.reduce((sum, it) => {
    const w = parseFloat(it.watts) || 0;
    const h = parseFloat(it.hours) || 0;
    const q = parseInt(it.quantity) || 1;
    return sum + w * h * q;
  }, 0);

  // ---------------- Design Assumptions ----------------
  const PSH = parseFloat(psh) || 5;
  const inverterSF = 1.25;        // inverter headroom
  const controllerSF = 1.3;       // controller safety factor
  const autonomyDays = 1;

  // Battery DoD (industry standard)
  const DoD = batteryType === "lithium" ? 0.8 : 0.5;
  const batteryEff = batteryType === "lithium" ? 0.95 : 0.85;

  // PV derating (heat, dust, wiring, MPPT loss)
  const pvDerate = 0.92;

  // ---------------- Inverter Sizing ----------------
  const inverterW = Math.ceil(totalLoadW * inverterSF);
  const rawKVA = (inverterW / 0.8) / 1000;
  const inverterKVA = getMarketInverter(rawKVA);

  const inverterNote =
    `Inverter sized with ${(inverterSF * 100).toFixed(0)}% headroom and efficiency factor (0.8) to accommodate surge loads and future expansion.`;

  // ---------------- Initial System Voltage Selection ----------------
  let systemVoltage;
  if (inverterW > 3000) systemVoltage = 48;
  else if (inverterW > 1200) systemVoltage = 24;
  else systemVoltage = 12;

  // ---------------- Battery Sizing ----------------
  const requiredBatteryWh =
    (totalLoadWh * autonomyDays) / (DoD * batteryEff);

  let batterySummary = "";
  let batteryConfigReason = "";

  if (batteryType === "lithium") {
    const requiredKwh = Math.ceil(requiredBatteryWh / 1000);
    batterySummary = `Total Required Battery Capacity: ${requiredKwh} kWh (Lithium)
- Depth of Discharge (DoD): ${(DoD * 100).toFixed(0)}%
- Battery efficiency considered: ${(batteryEff * 100).toFixed(0)}%`;

    batteryConfigReason =
      "Lithium batteries are sized by usable energy (kWh), support deeper discharge, and account for real-world efficiency losses.";
  }

  if (batteryType === "tubular") {
    const totalAh = Math.ceil(requiredBatteryWh / systemVoltage);
    const roundedAh = Math.ceil(totalAh / 10) * 10;
    const batteryCount = Math.ceil(roundedAh / 220);

    batterySummary = `Total Required Battery Capacity: ${roundedAh} Ah @ ${systemVoltage}V
Recommended Batteries: ${batteryCount} × 220Ah Tubular Batteries
- Depth of Discharge (DoD): ${(DoD * 100).toFixed(0)}%
- Battery efficiency considered: ${(batteryEff * 100).toFixed(0)}%`;

    batteryConfigReason =
      "Tubular batteries are sized based on system voltage, DoD, and efficiency losses.";
  }

  // ---------------- Panel Sizing ----------------
  const panelSpecs = {
    100: { Vmp: 17.5, Voc: 21 },
    150: { Vmp: 17.8, Voc: 22 },
    200: { Vmp: 18.0, Voc: 22.5 },
    250: { Vmp: 30.0, Voc: 36 },
    300: { Vmp: 31.0, Voc: 37 },
    350: { Vmp: 32.0, Voc: 38 },
    400: { Vmp: 33.0, Voc: 39 },
    450: { Vmp: 34.0, Voc: 40 },
    500: { Vmp: 35.0, Voc: 41 },
    550: { Vmp: 36.0, Voc: 42 },
    600: { Vmp: 37.0, Voc: 43 },
  };

  const { Vmp: panelVmp, Voc: panelVoc } =
    panelSpecs[panelWattage] || { Vmp: 18, Voc: 22 };

  const requiredPanelW = (totalLoadWh / PSH) / pvDerate;
  const panelCount = Math.ceil(requiredPanelW / panelWattage);
  const totalPanelCapacity = panelCount * panelWattage;

  // ---------------- MPPT Panel String Logic ----------------
  const mpptMinV = systemVoltage * 1.2;
  const mpptMaxV = systemVoltage * 4;
  const minSeries = Math.ceil(mpptMinV / panelVmp);
  const maxSeries = Math.floor(mpptMaxV / panelVoc);
  const panelsInSeries = Math.max(minSeries, 1);
  const parallelStrings = Math.ceil(panelCount / panelsInSeries);

  let panelConfig = "";
  let panelConfigReason = "";

  if (panelsInSeries > maxSeries) {
    panelConfig = "Panel voltage exceeds MPPT input range.";
    panelConfigReason =
      "Selected panel configuration cannot operate safely within MPPT voltage limits.";
  } else {
    panelConfig =
      `Connect ${panelsInSeries} panels in series × ${parallelStrings} parallel string(s).`;

    panelConfigReason =
      `Note: connecting in Series increases PV voltage for MPPT efficiency, while parallel increases current capacity.
• PV derate factor applied: ${(pvDerate * 100).toFixed(0)}%
• Inverter safety factor: ${(inverterSF * 100).toFixed(0)}%
• Controller safety factor: ${(controllerSF * 100).toFixed(0)}%`;
  }

  // ---------------- Controller Sizing ----------------
  // Panel current at maximum power point
const panelImp = panelWattage / panelVmp;

// MPPT controller current is based on PV-side current
const rawControllerAmps = Math.ceil(panelImp * parallelStrings * controllerSF);



  const standardMPPTs = [10, 20, 30, 40, 45, 60, 80, 100, 120];
  let controllerAmps = 0;
  let controllerNote = "";

  if (rawControllerAmps > 120) {
    // only use identical MPPTs in parallel
    const selectedMPPT = 120; // largest standard for residential/commercial
    const numControllers = Math.ceil(rawControllerAmps / selectedMPPT);

    controllerAmps = rawControllerAmps;

    controllerNote = `⚠️ Calculated controller current (${rawControllerAmps.toFixed(
      2
    )} A) exceeds 120 A. Suggest using ${numControllers} × ${selectedMPPT} A MPPTs in parallel.
Note: MPPTs must be identical; total current rounded up to ensure all PV current is safely handled.`;
  } else {
    // pick nearest standard MPPT
    controllerAmps = standardMPPTs.find(a => a >= rawControllerAmps) || standardMPPTs[standardMPPTs.length - 1];
  }

  const controllerType = "MPPT";
  const controllerReason =
    "MPPT controller allows higher PV voltage, improves efficiency, and supports flexible panel stringing.";

  // ---------------- Cable, Fuse & Switch ----------------
  const cableSizeMM =
    controllerAmps <= 20 ? 4 :
    controllerAmps <= 35 ? 6 :
    controllerAmps <= 55 ? 10 :
    controllerAmps <= 85 ? 16 : 25;

  const switchRating = Math.ceil(controllerAmps * 1.25);
  const fuseRating = Math.ceil(controllerAmps * 1.3);

  // ---------------- Final Result ----------------
  setResults({
    totalLoadW,
    totalLoadWh,
    inverterW,
    inverterKVA,
    inverterNote,
    systemVoltage,
    panelCount,
    totalPanelCapacity,
    batterySummary,
    batteryConfigReason,
    panelConfig,
    panelConfigReason,
    controllerType,
    controllerAmps,
    controllerNote,
    controllerReason,
    cableSizeMM,
    switchRating,
    fuseRating
  });

  setCalculating(false);
}, 600);


};




  // -------------- PDF --------------
const generatePDF = () => {
  if (!results) return;

  const doc = new jsPDF();
  doc.setFontSize(16);
  doc.text("Solar Sizing Report", 20, 20);
  doc.setFontSize(12);

  const lines = [
    `1. Total Load: ${results.totalLoadW} W`,
    `2. Energy: ${results.totalLoadWh.toFixed(2)} Wh/day`,
    `3. System Voltage: ${results.systemVoltage} V`,
    `4. Panels: ${results.panelCount} × ${panelWattage}W = ${results.totalPanelCapacity} W`,
    `5. Panel Config: ${results.panelConfig}`,
    `   Reason: ${results.panelConfigReason}`,
    `6. Battery Bank: ${results.batterySummary}`,
    `   Reason: ${results.batteryConfigReason}`,
    `7. Inverter Size: ${results.inverterKVA} kVA`,
    `8. Charge Controller: ${results.controllerAmps} Amps (${results.controllerType})`,
    `   Reason: ${results.controllerReason}`,
    `9. Cable Size: ${results.cableSizeMM} mm²`,
    `10. Switch Rating: ${results.switchRating} A`,
    `11. Fuse Rating: ${results.fuseRating} A`,
  ];

  lines.forEach((line, i) => doc.text(line, 20, 40 + i * 10));

  const blob = doc.output("blob");
  const url = URL.createObjectURL(blob);

  // Create temporary <a> element to trigger download
  const a = document.createElement("a");
  a.href = url;
  a.download = "Solar_Sizing_Report.pdf"; // filename for download
  document.body.appendChild(a);
  a.click(); // trigger download
  document.body.removeChild(a);
  URL.revokeObjectURL(url); // clean up
};




  // -------------- Mock PSH fetch --------------
  const fetchPSHAutomatically = () => {
  setGettingPSH(true);
  setTimeout(() => {
  setPsh("5");
  setGettingPSH(false);
  }, 1000);
  };

  // -------------- UI --------------
  const remainingDaysText = (() => {
  if (!subscriptionExpiry) return "No active subscription";
  const d = daysBetween(new Date(), new Date(subscriptionExpiry));
  if (d <= 0) return "Expired";
  return `${d} day${d === 1 ? "" : "s"} left`;
  })();


  // Full Page Return
  return (
    <div className="solar-calculator">
      <div className="hero">
        <h1>Solar Sizing Tips</h1>
        <div className="scrolling-tips">
          <ul>
            <li>1. Identify all appliances and their ratings</li>
            <li>2. Calculate total power and energy usage</li>
            <li>3. Understand your Peak Sun Hours (PSH)</li>
            <li>4. Select appropriate system voltage</li>
            <li>5. Size your battery bank for autonomy</li>
            <li>6. Match inverter capacity to total load</li>
            <li>7. Ensure solar panel output meets energy needs</li>
            <li>8. Choose the right charge controller (PWM or MPPT)</li>
            <li>9. Use proper wire sizes and fuse ratings</li>
            <li>10. Plan for expansion and future needs</li>
          </ul>
        </div>
      </div>

      <img src="/images/3.png" alt="" className="card-iconpanelservicecenters" />

      <h4>🔆 Full Solar Sizing Calculator</h4>

      <div className="calcsection">
        {items.map((item, idx) => (
          <div key={idx} className="appliance-row">
            <span>{idx + 1}.</span>
            <input required placeholder="Appliance Name" value={item.name} onChange={(e) => handleChange(idx, "name", e.target.value)} />
            <input required placeholder="Watts" type="number" value={item.watts} onChange={(e) => handleChange(idx, "watts", e.target.value)} />
            <input required placeholder="Hours/Day" type="number" value={item.hours} onChange={(e) => handleChange(idx, "hours", e.target.value)} />
            <input required placeholder="Quantity" type="number" value={item.quantity} onChange={(e) => handleChange(idx, "quantity", e.target.value)} />
            <button onClick={() => removeItem(idx)} className="remove-btn">Remove</button>
          </div>
        ))}

        <button className="add-btn" onClick={addItem}>+ Add Appliance</button>

        <div className="dropdown-section">
          <div className="dropdown-container">
            <FaSolarPanel size={24} color="#FFA500" style={{ marginRight: 8 }} />
            <select value={panelWattage} onChange={(e) => setPanelWattage(parseInt(e.target.value))}>
              <option value="">Select Preffered Panel Type</option>
              {panelOptions.map(w => (
                <option key={w} value={w}>{w}W</option>
              ))}
            </select>
          </div>

          <div className="dropdown-section">

  {/* Battery Type */}
  <div className="dropdown-container">
    <FaBatteryFull size={24} color="#4CAF50" style={{ marginRight: 8 }} />
    <select
      value={batteryType}
      onChange={(e) => {
        setBatteryType(e.target.value);
      }}
    >
      <option value="">Select Battery Type</option>
      <option value="tubular">Tubular Battery</option>
      <option value="lithium">Lithium Battery</option>
    </select>
  </div>

  

</div>

        </div>

        <div className="psh-input-container">
          <input className="psh-input" placeholder="Peak Sun Hours (e.g. 5)" type="number" value={psh} onChange={(e) => setPsh(e.target.value)} required />
          <button className="auto-psh-btn" onClick={fetchPSHAutomatically}>
            {gettingPSH ? "Getting PSH..." : "Use Automatic Peak Sun Hours"}
          </button>
        </div>

        <button
          className="calc-btn"
          onClick={calculateSizing}
          disabled={calculating}
        >
          {calculating ? "Calculating..." : "⚙️ Calculate System"}
        </button>

        {results && (
          <div className="results">
            <h3>📊 Sizing Results</h3>
            <p><strong>Total Load:</strong> {results.totalLoadW} W</p>
            <p><strong>Energy Use:</strong> {results.totalLoadWh.toFixed(2)} Wh/day</p>
            <p><strong>System Voltage:</strong> {results.systemVoltage} V</p>
            <p><strong>Panels:</strong> {results.panelCount} × {panelWattage}W = {results.totalPanelCapacity} W</p>
            {/* <p><strong>Panel Config:</strong> {results.panelConfig}</p> */}
            <p><em>{results.panelConfigReason}</em></p>
            <p><strong>Batteries:</strong> {results.batterySummary}</p>
            <p><em>{results.batteryConfigReason}</em></p>
            <p><strong>Inverter Size:</strong> {results.inverterKVA} kVA</p>
            <p><em>{results.inverterNote}</em></p>
            <p><strong>Controller:</strong> {results.controllerAmps} A ({results.controllerType})</p>

{results.controllerNote && (
  <p style={{ color: "#c0392b" }}>
    <em>{results.controllerNote}</em>
  </p>
)}

<p><em>{results.controllerReason}</em></p>

            <p><strong>Cable Size:</strong> {results.cableSizeMM} mm²</p>
            <p><strong>Switch/Fuse:</strong> {results.switchRating} A / {results.fuseRating} A</p>
            
            <button className="pdf-btn" onClick={generatePDF}>📄 Download PDF</button>


  
            <div className="share-section">
              <p><strong>📢 Share Your Result:</strong></p>
              <WhatsappShareButton url={shareUrl} title={shareTitle}><WhatsappIcon size={32} round /></WhatsappShareButton>
              <FacebookShareButton url={shareUrl} quote={shareTitle}><FacebookIcon size={32} round /></FacebookShareButton>
              <TwitterShareButton url={shareUrl} title={shareTitle}><TwitterIcon size={32} round /></TwitterShareButton>
              <LinkedinShareButton url={shareUrl} title={shareTitle}><LinkedinIcon size={32} round /></LinkedinShareButton>
            </div>
          </div>
        )}
      </div>

                                  <hr></hr>

      {/* Subscription status + controls */}
      <div className="subscription-card" style={{border:'1px, solid #eee', padding:12, borderRadius:5, marginBottom:16}}>
        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', gap:8, flexWrap:'wrap'}}>
          <div>
            <strong>Subscription:</strong>{" "}
            {isSubscribed ? "Active" : "Free tier"} — {remainingDaysText}
            {subscriptionExpiry && (
              <span style={{marginLeft:8, opacity:0.8}}>
                (expires {new Date(subscriptionExpiry).toDateString()})
              </span>
            )}
          </div>
          <div style={{display:'flex', gap:8}}>
            {!isSubscribed && (
              <button onClick={openSubscriptionModal} disabled={subLoading} className="calc-btn">
                {subLoading ? "Opening Paystack..." : "Subscribe to Unlock"}
              </button>
            )}
            {isSubscribed && (
              <button onClick={openSubscriptionModal} disabled={subLoading} className="calc-btn">
                {subLoading ? "Opening Paystack..." : "Extend Subscription"}
              </button>
            )}
          </div>
        </div>
        <div style={{marginTop:8}}>
          <input
            type="email"
            placeholder="Email for receipts & reminders"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={{padding:8, width:'100%', maxWidth:360}}
          />
          <div style={{fontSize:12, opacity:0.8, marginTop:4}}>
            We’ll remind you 5 days before expiry (in-app alert).
          </div>
        </div>
        {!isSubscribed && (
          <div style={{marginTop:8, fontSize:12, opacity:0.9}}>
            Free calculations used: {calcCount}/{trialLimit}
          </div>
        )}
      </div>

      <Modal
  isOpen={showModal}
  onRequestClose={() => setShowModal(false)}
  contentLabel="Choose a Plan"
  className="custom-modal"
  overlayClassName="custom-overlay"
>
  <h2 className="modal-title">Choose a Plan</h2>

  {plans.map((plan, idx) => (
    <button
      key={idx}
      onClick={() => handlePlanSelect(plan)}
      className="plan-button"
    >
      {plan.label} — ₦{plan.amountNGN}
    </button>
  ))}

  <button onClick={() => setShowModal(false)} className="cancel-button">
    Cancel
  </button>
</Modal>



<Modal
  isOpen={showEmailModal}
  onRequestClose={() => setShowEmailModal(false)}
  contentLabel="Enter Email"
  className="custom-modal"
  overlayClassName="custom-overlay"
>
  <h2 className="modal-title">Enter Your Email</h2>
  <input
    type="email"
    placeholder="Email for receipt & reminders"
    value={tempEmail}
    onChange={(e) => setTempEmail(e.target.value)}
    className="email-input"
  />
  <div className="modal-actions">
    <button
      onClick={() => setShowEmailModal(false)}
      className="cancel-button"
    >
      Cancel
    </button>
    <button
      onClick={() => {
        if (!tempEmail) return; // basic validation
        setEmail(tempEmail);
        localStorage.setItem("userEmail", tempEmail);
        setShowEmailModal(false);
        if (pendingPlan) {
          initiatePayment(pendingPlan); // continue payment after email
          setPendingPlan(null);
        }
      }}
      className="calc-btn2"
    >
      Submit
    </button>
  </div>
</Modal>




    </div>
  );
}
